import React, { useEffect, useState } from "react";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAppSelector } from "@/store/hooks";

interface Course {
  _id: string;
  slug: string;
  title: string;
  image?: string;
  description?: string;
  price: number;
  oldPrice?: number;
  validity?: string;
  languages?: string[];
  buyLink?: string;
}

const Courses: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  const { token } = useAppSelector((state) => state.auth);
  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const isApiSuccess = (res: any) =>
    res?.data?.status === true || res?.data?.success === true;

  // Fetch all courses
  useEffect(() => {
    if (!token) return;

    axios
      .get(`${baseUrl}/client/courses`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (isApiSuccess(res) && Array.isArray(res.data.data)) {
          setCourses(res.data.data);
        } else {
          setCourses([]);
          console.warn("Unexpected response:", res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch courses:", err.response?.data || err.message);
      });
  }, [baseUrl, token]);

  // Delete course
// Delete course by ID
const handleDelete = async (id: string) => {
  if (!window.confirm("Are you sure you want to delete this course?")) return;

  try {
    const res = await fetch(`${baseUrl}/courses/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    const data = await res.json();

    if (res.ok && data?.status) {
      setCourses(prev => prev.filter(c => c._id !== id));
      alert("✅ Course deleted successfully");
    } else {
      alert(data?.message || "Failed to delete course");
    }
  } catch (err) {
    console.error("❌ Error deleting course:", err);
    alert("Failed to delete course.");
  }
};

// Edit course by ID
const handleEdit = (id: string) => {
  navigate(`/courses/edit/${id}`);
};



  // Pagination + Search
  const filteredCourses = courses.filter(
    (course) =>
      course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCourses.length / entriesPerPage) || 1;
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const currentCourses = filteredCourses.slice(startIndex, endIndex);

  return (
    <div className="main-container container-fluid">
      {/* PAGE HEADER */}
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item">
            <a href="/dashboard">Home</a>
          </li>
          <li className="breadcrumb-item active">Courses</li>
        </ol>
        <button
          className="btn btn-primary d-flex align-items-center gap-2"
          onClick={() => navigate("/courses/add")}
        >
          <Plus size={16} /> Add Course
        </button>
      </div>

      {/* COURSES TABLE */}
      <div className="card">
        <div className="card-body">
          {/* Controls */}
          <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
            <div>
              Show{" "}
              <select
                className="form-select form-select-sm d-inline w-auto mx-1"
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>{" "}
              entries
            </div>
            <div>
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search Course..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Price</th>
                  <th>Validity</th>
                  <th>Languages</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentCourses.length ? (
                  currentCourses.map((course, idx) => (
                    <tr key={course._id}>
                      <td>{startIndex + idx + 1}</td>
                      <td>
                        {course.image ? (
                          <img
                            src={course.image}
                            alt={course.title}
                            style={{ height: "60px", borderRadius: "6px" }}
                          />
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td>{course.title}</td>
                      <td>
                        ₹{course.price}{" "}
                        {course.oldPrice && (
                          <small className="text-muted ms-1 text-decoration-line-through">
                            ₹{course.oldPrice}
                          </small>
                        )}
                      </td>
                      <td>{course.validity || "-"}</td>
                      <td>{course.languages?.join(", ") || "-"}</td>
                      <td>
                        <button
                          onClick={() => handleEdit(course._id)}
                          className="btn btn-sm btn-outline-primary me-2"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(course._id)}
                          className="btn btn-sm btn-outline-danger"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center">
                      No courses found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div>
              Showing {filteredCourses.length ? startIndex + 1 : 0} to{" "}
              {Math.min(endIndex, filteredCourses.length)} of {filteredCourses.length} entries
            </div>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                >
                  Prev
                </button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li
                  key={i}
                  className={`page-item ${i + 1 === currentPage ? "active" : ""}`}
                >
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
